/**
 * Device Service with TypeScript and Error Handling
 * Demonstrates the new approach compared to old DeviceContext.js
 */

import type { Device, TracePoint, DeviceCreateRequest, DeviceUpdateRequest } from '../types';

import { deviceAPI } from './apiClient';
import { handleError } from './errorHandling';
import logger from './logger';

/**
 * Service class for all device-related operations
 * Replaces scattered fetch calls with centralized, type-safe methods
 */
export class DeviceService {
  private static instance: DeviceService;

  static getInstance(): DeviceService {
    if (!DeviceService.instance) {
      DeviceService.instance = new DeviceService();
    }
    return DeviceService.instance;
  }

  /**
   * Fetch all devices with proper error handling
   * Compare this to the old DeviceContext.js fetchDevices method
   */
  async fetchDevices(): Promise<Device[]> {
    try {
      const data = await deviceAPI.getDevices();

      // Handle different response formats from backend
      // Backend might return array directly or object with devices property
      let deviceArray: unknown[] = [];
      if (Array.isArray(data)) {
        deviceArray = data;
      } else if (data && typeof data === 'object') {
        // If data is object, try to extract array from common property names
        const dataObj = data as Record<string, unknown>;
        const extracted = dataObj.devices || dataObj.data || dataObj.items;
        deviceArray = Array.isArray(extracted) ? extracted : [];
      }

      // Transform and validate data. Bind the instance methods so they keep `this`
      // when passed as array callbacks (defensive — L-6).
      const devices: Device[] = deviceArray
        .filter((d) => this.isValidDeviceData(d))
        .map((d) => this.transformToDevice(d))
        .sort((a, b) => this.sortDevicesAlphabetically(a, b));

      logger.info(`Successfully fetched ${devices.length} devices`);
      return devices;
    } catch (error) {
      // Centralized error handling automatically processes and reports the error
      const processedError = handleError(error);

      // Rethrow for component to handle UI state
      throw processedError;
    }
  }

  /**
   * Fetch device trace with type safety
   */
  async fetchTrace(deviceId: string): Promise<TracePoint[]> {
    try {
      const data = await deviceAPI.getTrace(deviceId);

      return data
        .filter((p) => this.isValidTracePoint(p))
        .map((p) => this.transformToTracePoint(p))
        .sort((a, b) => {
          const aTime =
            a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
          const bTime =
            b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
          return bTime - aTime;
        });
    } catch (error) {
      const processedError = handleError(error);
      throw processedError;
    }
  }

  /**
   * Add new device with validation
   */
  async addDevice(deviceData: DeviceCreateRequest): Promise<void> {
    try {
      // Validate input data
      this.validateDeviceCreateRequest(deviceData);

      // The /devices endpoint registers the device but does not return a device object
      // (it appears once it reports a position). Only surface errors here. (M-4)
      await deviceAPI.addDevice(deviceData);
    } catch (error) {
      const processedError = handleError(error);
      throw processedError;
    }
  }

  /**
   * Update existing device
   */
  async updateDevice(deviceId: string, updates: DeviceUpdateRequest): Promise<Device> {
    try {
      this.validateDeviceUpdateRequest(updates);

      const response = await deviceAPI.updateDevice(deviceId, updates);
      return this.transformToDevice(response);
    } catch (error) {
      const processedError = handleError(error);
      throw processedError;
    }
  }

  /**
   * Delete device
   */
  async deleteDevice(deviceId: string): Promise<void> {
    try {
      await deviceAPI.deleteDevice(deviceId);
    } catch (error) {
      const processedError = handleError(error);
      throw processedError;
    }
  }

  /**
   * Device tracking operations
   */
  async beginTracking(deviceId: string): Promise<void> {
    try {
      await deviceAPI.downlink(deviceId, 2, '9100000001');
    } catch (error) {
      const processedError = handleError(error);
      throw processedError;
    }
  }

  async deactivateTracking(deviceId: string): Promise<void> {
    try {
      await deviceAPI.downlink(deviceId, 2, '9100000000');
    } catch (error) {
      const processedError = handleError(error);
      throw processedError;
    }
  }

  /**
   * Check-in operations
   */
  async checkinDevice(deviceId: string, checkpoint: string): Promise<void> {
    try {
      await deviceAPI.checkin(deviceId, checkpoint);
    } catch (error) {
      const processedError = handleError(error);
      throw processedError;
    }
  }

  async uncheckDevice(deviceId: string): Promise<void> {
    try {
      await deviceAPI.uncheck(deviceId);
    } catch (error) {
      const processedError = handleError(error);
      throw processedError;
    }
  }

  // Private helper methods for data validation and transformation

  private isValidDeviceData(data: any): boolean {
    // Device must have id and title
    if (!data || typeof data.id !== 'string' || typeof data.title !== 'string') {
      return false;
    }

    // Fix 2025-10-08: Coordinates are optional (check-in stations may not have GPS coordinates)
    // Changed to allow null latlng - Map view will handle filtering devices without coordinates
    // If latlng exists, it must be valid
    if (data.latlng !== null && data.latlng !== undefined) {
      if (
        !Array.isArray(data.latlng) ||
        data.latlng.length !== 2 ||
        typeof data.latlng[0] !== 'number' ||
        typeof data.latlng[1] !== 'number'
      ) {
        return false;
      }
    }

    return true;
  }

  private isValidTracePoint(data: any): boolean {
    return (
      data &&
      typeof data.lat === 'number' &&
      typeof data.lng === 'number' &&
      (data.timestamp || data.captureTime || data.positionTime)
    );
  }

  private transformToDevice(data: any): Device {
    // Fix 2025-10-08: Handle coordinates - may be null for check-in stations
    // Check-in stations use deck/side/frame_number instead of GPS coordinates
    let latlng: [number, number] | null = null;
    if (data.latlng && Array.isArray(data.latlng) && data.latlng.length === 2) {
      latlng = [data.latlng[0], data.latlng[1]] as [number, number];
    }

    return {
      id: data.id,
      title: data.title,
      text: data.text || '',
      latlng: latlng,

      timestamp: new Date(data.timestamp),
      positionTime: new Date(data.positionTime || data.timestamp),
      alarmTime: data.alarmTime ? new Date(data.alarmTime) : undefined,
      checkinTime: data.checkinTime ? new Date(data.checkinTime) : undefined,
      traceTime: data.traceTime ? new Date(data.traceTime) : undefined,

      active: data.active,
      accuracy: data.accuracy,
      rssi: data.rssi,
      deviceType: data.deviceType,
      deviceCategory: data.deviceCategory, // Added 2025-10-08 for device categorization

      alarmAcknowledge: data.alarmAcknowledge
        ? {
            username: data.alarmAcknowledge.username,
            timestamp: new Date(data.alarmAcknowledge.timestamp),
          }
        : undefined,

      checkpoint: data.checkpoint,
      checkinCheckpoint: data.checkinCheckpoint,
      canAcknowledge: data.canAcknowledge,

      output: data.output,
      floor: data.floor,
      area: data.area || null,
      frame: data.frame ?? null,
      positionType: data.positionType,
      positionFiltered: data.positionFiltered,
      positionReplaced: data.positionReplaced,
    };
  }

  private transformToTracePoint(data: any): TracePoint {
    const timestamp = data.captureTime || data.timestamp || data.positionTime;

    return {
      id: data.id || `${data.lat}-${data.lng}-${timestamp}`,
      lat: data.lat,
      lng: data.lng,
      timestamp: new Date(timestamp),
      accuracy: data.accuracy,
      floor: data.floor,
      positionFiltered: data.positionFiltered,
      positionReplaced: data.positionReplaced,
      captureTime: data.captureTime ? new Date(data.captureTime) : undefined,
      addedTimestamp: data.addedTimestamp ? new Date(data.addedTimestamp) : undefined,
      status: data.status,
    };
  }

  private sortDevicesAlphabetically(a: Device, b: Device): number {
    const aName = a.text || a.title;
    const bName = b.text || b.title;
    return aName.localeCompare(bName);
  }

  private validateDeviceCreateRequest(data: DeviceCreateRequest): void {
    if (!data.title || data.title.trim().length < 1) {
      throw new Error('Device title is required');
    }
    if (data.title.length > 100) {
      throw new Error('Device title must be less than 100 characters');
    }
  }

  private validateDeviceUpdateRequest(data: DeviceUpdateRequest): void {
    if (data.title && data.title.length > 100) {
      throw new Error('Device title must be less than 100 characters');
    }
  }
}

// Singleton instance for easy importing
export const deviceService = DeviceService.getInstance();

/**
 * Usage Example:
 *
 * // Old way (DeviceContext.js):
 * const fetchDevices = useCallback(async () => {
 *   if (!token || !isAuthenticated) return;
 *   setIsLoading(true);
 *   setError(null);
 *   try {
 *     const response = await fetch(`${getApi()}/devices?token=${token}`);
 *     if (!response.ok) { throw new Error(...) }
 *     const data = await response.json();
 *     if (data.error) { throw new Error(data.error) }
 *     // Manual error handling, no types, repetitive code
 *   } catch (error) {
 *     setError(error.message); // Basic error handling
 *   } finally {
 *     setIsLoading(false);
 *   }
 * }, [token, isAuthenticated]);
 *
 * // New way:
 * const fetchDevices = useCallback(async () => {
 *   if (!isAuthenticated) return;
 *   setIsLoading(true);
 *   setError(null);
 *   try {
 *     const devices = await deviceService.fetchDevices();
 *     setDevices(devices); // Fully typed devices
 *   } catch (error) {
 *     // Error is already processed and reported centrally
 *     setError(error.userMessage); // User-friendly message
 *   } finally {
 *     setIsLoading(false);
 *   }
 * }, [isAuthenticated]);
 */
